import { useTranslation } from 'react-i18next';
import { Loader, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconExternalLink, IconClock, IconGitBranch } from '@tabler/icons-react';
import { useReservation } from '@/hooks/api-hooks';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { formatDayShort } from '@/lib/format-utils';

interface ParentReservationMinimapProps {
    parentId: number;
}

export default function ParentReservationMinimap({ parentId }: ParentReservationMinimapProps) {
    const { t } = useTranslation();
    const { data: resData, isLoading } = useReservation(parentId);
    const parentReservation = resData?.data;

    if (isLoading) {
        return (
            <div className="data-card flex items-center justify-center gap-2">
                <Loader size="xs" color="blue" />
                <span className="text-xs text-gray-500 font-medium">{t('loading')}</span>
            </div>
        );
    }

    if (!parentReservation) {
        return null;
    }

    const bookings = parentReservation.service_bookings || [];

    return (
        <div className="data-card space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div className="flex items-center gap-1.5 min-w-0">
                    <IconGitBranch size={14} className="text-blue-500 shrink-0" />
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wider truncate">
                        {t('parent_reservation_bookings')}
                    </span>
                </div>
                <Link
                    to={`/reservations/${parentId}`}
                    target="_blank"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors shrink-0"
                >
                    <span>#{parentId}</span>
                    <IconExternalLink size={12} />
                </Link>
            </div>

            {bookings.length === 0 ? (
                <div className="text-xs text-gray-400 italic py-2 text-center">
                    {t('no_bookings_in_parent')}
                </div>
            ) : (
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                    {bookings.map((booking) => {
                        const serviceTitle = ServiceTitleStatic(booking.service_data);
                        const category = booking.service_data?.category || 'other';

                        return (
                            <div
                                key={booking.id}
                                className="p-2 bg-slate-50/60 hover:bg-slate-100/60 border border-slate-200/60 rounded-lg transition-all space-y-1"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <TagIcon size="sm" name={category} noBackground hideText />
                                        <Text size="xs" className="font-semibold text-slate-800 truncate">
                                            {serviceTitle}
                                        </Text>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between text-[10px] text-slate-500 pt-0.5">
                                    <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                                        <span>{formatDayShort(booking.start_day)}</span>
                                        {booking.start_hour && (
                                            <span className="flex items-center gap-0.5 text-slate-400">
                                                <IconClock size={10} />
                                                {booking.start_hour}
                                            </span>
                                        )}
                                    </div>
                                    <Link
                                        to={`/service-bookings/${booking.id}`}
                                        target="_blank"
                                        className="text-[10px] text-blue-500 hover:underline "
                                    >
                                        #{booking.id}
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
